import { runManyRows, type SqliteExecutor, type SqlValue } from './sqliteExecutor.ts'

// Offline `filteredNovelties` joins the catalog replica against the user's
// collection. The collection lives in a Pinia/idb store, so we mirror the slices
// the join needs into the same SQLite DB, using the EXACT table/column names the
// Postgres schema uses — that lets the shared atsume-query-core query render
// identically to both dialects (server LATERAL join vs SQLite, same SQL shape).
//
// - UserReleaseEntryStatus: one row per collected entry (itemStatus/miscItemStatus
//   ownership maps, collectionStatus, the user's collecting window startDate/endDate).
// - UserReleaseReadStatus: the latest read map per entry (the client already
//   collapses history to the latest, so exactly one row per entry — keyed by the
//   UserReleaseEntryStatus row id, matching the server's `urrs.entry = urs.id`).
// - _ReleaseItem_collections: the Prisma implicit m2m membership (A = ReleaseItem
//   id, B = ReleaseItemCollection id), derived from collection deltas — used by the
//   "hide alternatives when owned" branch.
// The optimistic collection state for one entry, flattened to the server's row
// shape. Built from the user-collection store's collectionMap so the mirror
// reflects unsynced local edits, with the collecting window carried from the
// synced UserReleaseEntryStatus row.
export type MirrorEntry = {
  id: string
  entryId: string
  itemStatus: Record<string, number>
  miscItemStatus: Record<string, { type: string; quantity: number }>
  collectionStatus: string
  startDate: string | null
  endDate: string | null
  readStatus: Record<string, boolean>
  // Denormalized UserReleaseEntryStatus scalars the collection-filtered query
  // filters/sorts on (offline filteredUserReleaseEntryStatuses). Optional so
  // novelty-only mirror callers (parity fixtures) need not supply them.
  currentReadStatus?: string | null
  internalName?: string | null
  publisherName?: string | null
  type?: string | null
  tagSearchField?: string | null
  demographySearchField?: string | null
  titleSearch?: string | null
  totalVolumes?: number | null
  totalNotReleasedVolumes?: number | null
  totalReleasedVolumes?: number | null
  lastCollectionChanged?: string | null
  lastReadChanged?: string | null
  createdAt?: string | null
  compoundKey?: string | null
  popularityScore?: number | null
  recommendationScore?: number | null
}

// Single source of truth for the UserReleaseEntryStatus mirror table: the column
// name + SQLite type + how to extract the value from a MirrorEntry. The DDL and
// the INSERT both derive from this, so adding a filter/sort column is a one-line
// change (no separate CREATE / column-list / values edits to keep in sync).
type MirrorColumn = {
  col: string
  type: 'TEXT' | 'INTEGER' | 'REAL'
  notNull?: boolean
  primaryKey?: boolean
  value: (entry: MirrorEntry) => SqlValue
}

const URS_MIRROR_COLUMNS: readonly MirrorColumn[] = [
  { col: 'id', type: 'TEXT', primaryKey: true, notNull: true, value: (e) => e.id },
  { col: 'entry', type: 'TEXT', notNull: true, value: (e) => e.entryId },
  { col: 'itemStatus', type: 'TEXT', notNull: true, value: (e) => JSON.stringify(e.itemStatus ?? {}) },
  {
    col: 'miscItemStatus',
    type: 'TEXT',
    notNull: true,
    value: (e) => JSON.stringify(e.miscItemStatus ?? {}),
  },
  { col: 'collectionStatus', type: 'TEXT', value: (e) => e.collectionStatus },
  { col: 'currentReadStatus', type: 'TEXT', value: (e) => e.currentReadStatus ?? null },
  { col: 'startDate', type: 'TEXT', value: (e) => e.startDate },
  { col: 'endDate', type: 'TEXT', value: (e) => e.endDate },
  { col: 'internalName', type: 'TEXT', value: (e) => e.internalName ?? null },
  { col: 'publisherName', type: 'TEXT', value: (e) => e.publisherName ?? null },
  { col: 'type', type: 'TEXT', value: (e) => e.type ?? null },
  { col: 'tagSearchField', type: 'TEXT', value: (e) => e.tagSearchField ?? null },
  { col: 'demographySearchField', type: 'TEXT', value: (e) => e.demographySearchField ?? null },
  { col: 'titleSearch', type: 'TEXT', value: (e) => e.titleSearch ?? null },
  { col: 'totalVolumes', type: 'INTEGER', value: (e) => e.totalVolumes ?? null },
  { col: 'totalNotReleasedVolumes', type: 'INTEGER', value: (e) => e.totalNotReleasedVolumes ?? null },
  { col: 'totalReleasedVolumes', type: 'INTEGER', value: (e) => e.totalReleasedVolumes ?? null },
  { col: 'lastCollectionChanged', type: 'TEXT', value: (e) => e.lastCollectionChanged ?? null },
  { col: 'lastReadChanged', type: 'TEXT', value: (e) => e.lastReadChanged ?? null },
  { col: 'createdAt', type: 'TEXT', value: (e) => e.createdAt ?? null },
  { col: 'compoundKey', type: 'TEXT', value: (e) => e.compoundKey ?? null },
  { col: 'popularityScore', type: 'REAL', value: (e) => e.popularityScore ?? null },
  { col: 'recommendationScore', type: 'REAL', value: (e) => e.recommendationScore ?? null },
]

function ursMirrorTableDDL(): string {
  const columns = URS_MIRROR_COLUMNS.map((c) => {
    const modifiers = `${c.primaryKey ? ' PRIMARY KEY' : ''}${c.notNull ? ' NOT NULL' : ''}`
    return `  "${c.col}" ${c.type}${modifiers}`
  }).join(',\n')
  return `CREATE TABLE IF NOT EXISTS "UserReleaseEntryStatus" (\n${columns}\n);`
}

export const USER_DATA_DDL: readonly string[] = [
  ursMirrorTableDDL(),
  `CREATE INDEX IF NOT EXISTS "UserReleaseEntryStatus_entry_idx" ON "UserReleaseEntryStatus"("entry");`,
  `CREATE TABLE IF NOT EXISTS "UserReleaseReadStatus" (
  "entry" TEXT NOT NULL,
  "readStatus" TEXT NOT NULL,
  "lastReadChanged" TEXT
);`,
  `CREATE INDEX IF NOT EXISTS "UserReleaseReadStatus_entry_idx" ON "UserReleaseReadStatus"("entry");`,
  `CREATE TABLE IF NOT EXISTS "_ReleaseItem_collections" (
  "A" TEXT NOT NULL,
  "B" TEXT NOT NULL
);`,
  `CREATE INDEX IF NOT EXISTS "_ReleaseItem_collections_A_idx" ON "_ReleaseItem_collections"("A");`,
  `CREATE INDEX IF NOT EXISTS "_ReleaseItem_collections_B_idx" ON "_ReleaseItem_collections"("B");`,
  // Curated novelty-list membership, mirrored so offline filteredCustomListNovelties
  // can join the catalog replica against a list's members. Single-user, so no
  // `user` column (the server filters on it; the client query passes TRUE).
  `CREATE TABLE IF NOT EXISTS "UserCustomListNoveltyItem" (
  "list" TEXT NOT NULL,
  "novelty" TEXT NOT NULL
);`,
  `CREATE INDEX IF NOT EXISTS "UserCustomListNoveltyItem_list_idx" ON "UserCustomListNoveltyItem"("list");`,
  // Curated release-entry-list membership, mirrored so offline
  // filteredCustomListReleaseEntries can join the catalog ReleaseEntry replica
  // against a list's members. Single-user → no `user` column.
  `CREATE TABLE IF NOT EXISTS "UserCustomListReleaseEntryItem" (
  "list" TEXT NOT NULL,
  "releaseEntry" TEXT NOT NULL
);`,
  `CREATE INDEX IF NOT EXISTS "UserCustomListReleaseEntryItem_list_idx" ON "UserCustomListReleaseEntryItem"("list");`,
]

export async function mirrorUserCollection(
  executor: SqliteExecutor,
  entries: readonly MirrorEntry[],
): Promise<void> {
  await executor.transaction(async () => {
    await executor.run('DELETE FROM "UserReleaseEntryStatus"', [])
    await executor.run('DELETE FROM "UserReleaseReadStatus"', [])
    const columnList = URS_MIRROR_COLUMNS.map((c) => `"${c.col}"`).join(', ')
    const placeholders = URS_MIRROR_COLUMNS.map(() => '?').join(', ')
    await runManyRows(
      executor,
      `INSERT INTO "UserReleaseEntryStatus" (${columnList}) VALUES (${placeholders})`,
      entries.map((entry) => URS_MIRROR_COLUMNS.map((c) => c.value(entry))),
    )
    await runManyRows(
      executor,
      `INSERT INTO "UserReleaseReadStatus" ("entry", "readStatus", "lastReadChanged")
       VALUES (?, ?, ?)`,
      entries.map((entry) => [entry.id, JSON.stringify(entry.readStatus ?? {}), null]),
    )
  })
}

// Replace collections' membership rows from a delta. Runs inside applyDelta's
// transaction so it commits atomically with the collection upserts; bulked so a
// bootstrap's thousands of collections cost a handful of native calls.
export async function replaceCollectionMemberships(
  executor: SqliteExecutor,
  membershipByCollection: ReadonlyMap<string, readonly string[]>,
): Promise<void> {
  if (membershipByCollection.size === 0) return
  await runManyRows(
    executor,
    'DELETE FROM "_ReleaseItem_collections" WHERE "B" = ?',
    [...membershipByCollection.keys()].map((collectionId) => [collectionId]),
  )
  const edges: [string, string][] = []
  for (const [collectionId, itemIds] of membershipByCollection) {
    for (const itemId of itemIds) edges.push([itemId, collectionId])
  }
  await runManyRows(executor, 'INSERT INTO "_ReleaseItem_collections" ("A", "B") VALUES (?, ?)', edges)
}

export async function deleteCollectionMemberships(
  executor: SqliteExecutor,
  collectionIds: readonly string[],
): Promise<void> {
  await runManyRows(
    executor,
    'DELETE FROM "_ReleaseItem_collections" WHERE "B" = ?',
    collectionIds.map((collectionId) => [collectionId]),
  )
}

// Refresh ONE curated novelty-list's membership rows just-in-time from the
// optimistic user-lists store, right before the filtered query joins them. Scoped
// to the list (not a full-table replace) and populated at read time, so the join
// scales to any list size while always reflecting the latest optimistic edit —
// no background mirror, no staleness.
// Deliberately NOT wrapped in executor.transaction(): that commits AND flushes the
// whole DB to IndexedDB, which is far too expensive to pay on a read path (and its
// beginTransaction collides with a concurrent bootstrap/sync — the DB has one
// transaction slot). These rows are ephemeral scratch, re-derived from the
// optimistic store whenever needed, so they never need to survive a reload.
export async function refreshListNoveltyMembership(
  executor: SqliteExecutor,
  listId: string,
  noveltyIds: readonly string[],
): Promise<void> {
  await executor.run('DELETE FROM "UserCustomListNoveltyItem" WHERE "list" = ?', [listId])
  await runManyRows(
    executor,
    'INSERT INTO "UserCustomListNoveltyItem" ("list", "novelty") VALUES (?, ?)',
    noveltyIds.map((noveltyId) => [listId, noveltyId]),
  )
}

export async function refreshListReleaseEntryMembership(
  executor: SqliteExecutor,
  listId: string,
  releaseEntryIds: readonly string[],
): Promise<void> {
  await executor.run('DELETE FROM "UserCustomListReleaseEntryItem" WHERE "list" = ?', [listId])
  await runManyRows(
    executor,
    'INSERT INTO "UserCustomListReleaseEntryItem" ("list", "releaseEntry") VALUES (?, ?)',
    releaseEntryIds.map((releaseEntryId) => [listId, releaseEntryId]),
  )
}
